/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

export interface AnimationStep {
  stringIndex: number; // 0-based array index
  fret: number;
  note: string;
  interval: string;
  isOctave: boolean; // Pause after this note
}

interface ScaleNote {
  stringIndex: number; // 0-based array index
  fret: number;
  note: string;
  interval: string;
}

// Helper to get chromatic index of a note
const CANONICAL_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
const BASE_NOTE_OFFSETS: Record<string, number> = {
  C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11
};

const getChromaticIndex = (noteName: string): number => {
  if (!noteName) return -1;
  const trimmed = noteName.trim();
  if (trimmed.length === 0) return -1;

  const letter = trimmed[0].toUpperCase();
  const baseOffset = BASE_NOTE_OFFSETS[letter];
  if (baseOffset === undefined) return -1;

  let semitone = baseOffset;
  for (let i = 1; i < trimmed.length; i++) {
    const char = trimmed[i];
    if (char === '#' || char === '♯') semitone += 1;
    else if (char === 'b' || char === '♭') semitone -= 1;
    else if (char === 'n' || char === '♮' || char === ' ') continue;
    else if (char >= '0' && char <= '9') break;
    else break;
  }

  return ((semitone % 12) + 12) % 12;
};

const getFretNumber = (stringNote: string, noteName: string): number => {
  const stringIndex = getChromaticIndex(stringNote);
  const noteIndex = getChromaticIndex(noteName);
  if (stringIndex === -1 || noteIndex === -1) return -1;
  return (noteIndex - stringIndex + 12) % 12;
};

/**
 * Generate animation sequence for scale pattern visualization
 * Adapts to any scale, tuning, and instrument configuration
 *
 * Algorithm:
 * 1. For each note in the scale, find all positions on the fretboard
 * 2. Group positions by string
 * 3. Find root note positions to start each pattern
 * 4. Create ascending sequences until octave
 * 5. Mark octaves for pause
 */
export function generateScaleAnimationSequence(
  noteCollection: any,
  strings: number,
  fretRange: { start: number; end: number },
  tuning: string[]
): AnimationStep[] {
  // console.log('generateScaleAnimationSequence called with:', {
  //   noteCollection,
  //   strings,
  //   fretRange,
  //   tuning
  // });

  if (!noteCollection?.notesAndIntervals || !tuning || tuning.length === 0) {
    // console.warn('Missing noteCollection or tuning');
    return [];
  }

  const steps: AnimationStep[] = [];

  // Build scale degree sequence (in order)
  const scaleSequence = noteCollection.notesAndIntervals.map((item: any) => ({
    note: item.note.note,
    interval: item.interval.abbreviation,
    semitones: item.interval.semitonesFromRoot
  }));

  // console.log('Scale sequence:', scaleSequence);

  // Build a map of all scale notes with their intervals
  const scaleNotesMap = new Map<string, string>();
  scaleSequence.forEach(item => {
    scaleNotesMap.set(item.note, item.interval);
  });

  // console.log('Scale notes map:', scaleNotesMap);

  // Find all positions where scale notes appear on the fretboard
  const scaleNotes: ScaleNote[] = [];

  tuning.forEach((stringNote, stringIndex) => {
    // For each note in the scale
    scaleNotesMap.forEach((intervalAbbr, scalNote) => {
      // Calculate base fret for this note on this string
      const baseFret = getFretNumber(stringNote, scalNote);
      if (baseFret === -1) return;

      // Add all octaves within the fret range
      for (let octave = 0; octave <= 2; octave++) {
        const fret = baseFret + (octave * 12);
        if (fret >= fretRange.start && fret <= fretRange.end) {
          scaleNotes.push({
            stringIndex,
            fret,
            note: scalNote,
            interval: intervalAbbr
          });
        }
      }
    });
  });

  // console.log('Found scale notes on fretboard:', scaleNotes);

  // Group notes by string (already in order from low to high pitch)
  const notesByString: Map<number, ScaleNote[]> = new Map();
  scaleNotes.forEach(note => {
    if (!notesByString.has(note.stringIndex)) {
      notesByString.set(note.stringIndex, []);
    }
    notesByString.get(note.stringIndex)!.push(note);
  });

  // Sort notes on each string by fret
  notesByString.forEach(notes => {
    notes.sort((a, b) => a.fret - b.fret);
  });

  // Find all root notes (interval "I" or "1" or "R") for position starts
  const rootNotes = scaleNotes
    .filter(note => note.interval === 'I' || note.interval === '1' || note.interval === 'R')
    .sort((a, b) => {
      // Sort by string (low to high), then by fret
      if (a.stringIndex !== b.stringIndex) {
        return a.stringIndex - b.stringIndex;
      }
      return a.fret - b.fret;
    });

  // console.log('Found root notes:', rootNotes);

  // Detect if this is a pentatonic scale (5 notes)
  const isPentatonic = scaleSequence.length === 5;
  const notesPerString = isPentatonic ? 2 : 3;

  // console.log(`Scale type: ${isPentatonic ? 'Pentatonic' : 'Standard'} (${scaleSequence.length} notes) - ${notesPerString} notes per string`);

  // For each root note, create an ascending sequence until next octave
  rootNotes.forEach((rootNote, rootIndex) => {
    const positionSteps = generatePositionSequence(
      rootNote,
      scaleNotes,
      scaleSequence,
      strings,
      notesPerString
    );

    // Mark last note as octave (for pause)
    if (positionSteps.length > 0) {
      positionSteps[positionSteps.length - 1].isOctave = true;
    }

    steps.push(...positionSteps);
  });

  // console.log('Generated animation steps:', steps.length);
  return steps;
}

/**
 * Generate ascending sequence from a root note until next octave
 * Uses n-notes-per-string pattern (2 for pentatonic, 3 for standard)
 */
function generatePositionSequence(
  startRoot: ScaleNote,
  allScaleNotes: ScaleNote[],
  scaleSequence: Array<{ note: string; interval: string; semitones: number }>,
  totalStrings: number,
  notesPerString: number
): AnimationStep[] {
  const sequence: AnimationStep[] = [];

  // Define the position range
  // Pentatonics need wider range (especially below) due to 2-notes-per-string spreading
  const isPentatonic = notesPerString === 2;
  const positionFretRange = {
    min: Math.max(0, startRoot.fret - (isPentatonic ? 4 : 2)),
    max: startRoot.fret + 6
  };

  // console.log(`Position starting at fret ${startRoot.fret}, range: ${positionFretRange.min}-${positionFretRange.max}`);

  // Filter to only notes within this position
  const notesInPosition = allScaleNotes.filter(note =>
    note.fret >= positionFretRange.min && note.fret <= positionFretRange.max
  );

  // Group notes by string
  const notesByString = new Map<number, ScaleNote[]>();
  notesInPosition.forEach(note => {
    if (!notesByString.has(note.stringIndex)) {
      notesByString.set(note.stringIndex, []);
    }
    notesByString.get(note.stringIndex)!.push(note);
  });

  // Sort notes on each string by fret (ascending)
  notesByString.forEach(notes => notes.sort((a, b) => a.fret - b.fret));

  let currentScaleDegreeIndex = 0; // Track position in scale (0 = root)
  let currentStringIndex = startRoot.stringIndex;
  let lastFret = -1;

  // Process each string starting from the root's string
  for (let stringIdx = startRoot.stringIndex; stringIdx < totalStrings; stringIdx++) {
    const notesOnString = notesByString.get(stringIdx) || [];

    // Filter to notes higher than last fret (or starting note for first string)
    const availableNotes = notesOnString.filter(note =>
      note.fret > lastFret || (stringIdx === startRoot.stringIndex && note.fret >= startRoot.fret)
    );

    if (availableNotes.length === 0) continue;

    // console.log(`String ${stringIdx}: ${availableNotes.length} available notes`);

    // Play notes on this string in ascending scale degree order
    let notesPlayedOnString = 0;
    const maxNotesPerString = notesPerString; // 2 for pentatonic, 3 for standard scales

    while (true) {
      // Find the next scale degree we need
      const targetScaleDegree = scaleSequence[currentScaleDegreeIndex];

      // Find this scale degree on the current string, higher than last fret
      const nextNote = availableNotes.find(note =>
        note.note === targetScaleDegree.note &&
        (note.fret > lastFret || (stringIdx === startRoot.stringIndex && notesPlayedOnString === 0))
      );

      if (!nextNote) {
        // This scale degree isn't available on this string, move to next string
        // console.log(`Scale degree ${targetScaleDegree.note} not found on string ${stringIdx}, moving to next string`);
        break;
      }

      // Check if we've hit the notes-per-string limit
      if (notesPlayedOnString >= maxNotesPerString) {
        // Check if we should make an exception to avoid octave jumps
        // Look ahead: where would this note be on the next string?
        const nextStringIdx = stringIdx + 1;
        if (nextStringIdx < totalStrings) {
          const notesOnNextString = notesByString.get(nextStringIdx) || [];
          const noteOnNextString = notesOnNextString.find(n => n.note === targetScaleDegree.note);

          if (noteOnNextString) {
            // Compare positions: if next string would be at much higher fret, stay on current string
            const currentStringFret = nextNote.fret;
            const nextStringFret = noteOnNextString.fret;

            if (nextStringFret > currentStringFret + 3) {
              // Next string would jump up 3+ frets - stay on current string to avoid octave jump
              // console.log(`Staying on string ${stringIdx} for ${nextNote.note}: current fret ${currentStringFret} vs next string fret ${nextStringFret} (avoiding jump)`);
            } else {
              // Next string is at similar or lower fret - move there for smooth position
              // console.log(`Moving to next string: ${nextNote.note} at fret ${nextStringFret} is smoother than fret ${currentStringFret}`);
              break;
            }
          } else {
            // Next note not available on next string, stay on current
            // console.log(`Staying on string ${stringIdx}: ${nextNote.note} not available on next string`);
          }
        } else {
          // No next string, stay here
          // console.log(`Staying on string ${stringIdx}: no more strings`);
        }
      }

      // Check if this note is an octave (root note but not the starting note)
      const isOctaveNote = currentScaleDegreeIndex === 0 && sequence.length > 0;

      // Add this note to sequence
      sequence.push({
        stringIndex: nextNote.stringIndex,
        fret: nextNote.fret,
        note: nextNote.note,
        interval: nextNote.interval,
        isOctave: false
      });

      // console.log(`Added: ${nextNote.note} at string ${stringIdx} fret ${nextNote.fret}${isOctaveNote ? ' (OCTAVE)' : ''}`);

      lastFret = nextNote.fret;
      notesPlayedOnString++;

      // If we just added an octave, stop this position
      if (isOctaveNote) {
        // console.log('Reached octave, stopping position');
        return sequence;
      }

      // Move to next scale degree
      currentScaleDegreeIndex = (currentScaleDegreeIndex + 1) % scaleSequence.length;
    }

    // Reset fret tracking when moving to next string
    lastFret = -1;
  }

  // console.log('Position sequence length:', sequence.length);
  return sequence;
}

/**
 * Calculate delay for animation step
 * Longer pause at octave points
 */
export function getStepDelay(step: AnimationStep): number {
  return step.isOctave ? 1200 : 600; // 1200ms pause at octave, 600ms normal
}
